import { createSelectorCreator, defaultMemoize } from 'reselect';
import { cloneDeep, isEqual } from 'lodash';
import moment from 'moment';

import { includeIntacctId } from '../../helpers/utils';
import { TimesheetEntriesStore, TimesheetEntriesStoreItem } from '../../types/redux/stores/timesheetEntries.types';

// create a "selector creator" that uses lodash.isEqual instead of ===
const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const filterAssignedClusters = (clusters: any[], entries: TimesheetEntriesStore['data'], timesheetId: string) => {
  return clusters.filter((c) => {
    if (c.timesheet_entry_id) {
      const existingEntry = entries[c.timesheet_entry_id];
      return existingEntry?.timesheet_id === timesheetId;
    }
    return !!c.miscellaneousReason;
  });
};

const filterUnAssignedClusters = (clusters: any[]) => {
  return clusters.filter((c) => {
    return !(!!c.timesheet_entry_id || !!c.miscellaneousReason);
  });
};

const groupAndSortClusters = (clusters: any[]) => {
  const groupedByDate = clusters.reduce((groups, cluster) => {
    const date = cluster.date;
    if (!groups[date]) groups[date] = [];
    groups[date].push(cluster);
    return groups;
  }, {});

  const groupedByDateArray = Object.keys(groupedByDate)
    .map((date) => {
      return {
        date,
        clusters: sortClustersHelper(groupedByDate[date]),
      };
    })
    .sort((a, b) => {
      const diff = moment(a.date, 'YYYY-MM-DD').diff(moment(b.date, 'YYYY-MM-DD'));
      if (diff <= 0) {
        return 1;
      } else {
        return -1;
      }
    });
  return groupedByDateArray;
};

const sortClustersHelper = (clusters: any[]) => {
  return clusters.sort((a, b) => {
    if (a.source === 'calendar') {
      if (b.source !== 'calendar') return -1;
      const diff = moment(a.metaData?.timestamp_end || a.createdAt).diff(moment(b.metaData?.timestamp_end || b.createdAt));
      if (diff < 0) {
        return 1;
      } else if (diff > 0) {
        return -1;
      } else {
        return a.id - b.id;
      }
    } else if (b.source === 'calendar') {
      return -1;
    } else if (a.source === 'email') {
      if (b.source !== 'email') return -1;
      const diff = moment(a.metaData?.last_timestamp_end || a.createdAt).diff(moment(b.metaData?.last_timestamp_end || b.createdAt));
      if (diff < 0) {
        return 1;
      } else if (diff > 0) {
        return -1;
      } else {
        return a.id - b.id;
      }
    } else if (b.source === 'email') {
      return 1;
    } else if (a.source === 'activity') {
      if (b.source !== 'activity') return 1;
      const diff = moment(a.metaData?.last_timestamp_end || a.createdAt).diff(moment(b.metaData?.last_timestamp_end || b.createdAt));
      if (diff < 0) {
        return 1;
      } else if (diff > 0) {
        return -1;
      } else {
        return a.id - b.id;
      }
    } else {
      // fallback
      const diff = moment(a.createdAt).diff(moment(b.createdAt));
      if (diff < 0) {
        return 1;
      } else if (diff > 0) {
        return -1;
      } else {
        return a.id - b.id;
      }
    }
  });
};

const includeIntacctIdInClusterDimensionNames = (cluster: any = {}) => {
  if (!cluster || !cluster.id) return;
  const clusterCopy = cloneDeep(cluster);
  if (clusterCopy.predictedClientId && clusterCopy.client?.name && clusterCopy.client?.externalId) {
    clusterCopy.client = includeIntacctId(clusterCopy.client);
  }
  if (clusterCopy.predictedProjectId && clusterCopy.project?.name && clusterCopy.project?.externalId) {
    clusterCopy.project = includeIntacctId(clusterCopy.project);
  }
  if (clusterCopy.predictedTaskId && clusterCopy.task?.name && clusterCopy.task?.externalId) {
    clusterCopy.task = includeIntacctId(clusterCopy.task);
  }
  if (clusterCopy.predictedItemId && clusterCopy.item?.name && clusterCopy.item?.externalId) {
    clusterCopy.item = includeIntacctId(clusterCopy.item);
  }
  return clusterCopy;
};

const includeIntacctIdInEntryDimensionNames = (entry: TimesheetEntriesStoreItem): TimesheetEntriesStoreItem | null => {
  if (!entry || !entry.id) return null;
  const entryCopy = cloneDeep(entry);
  if (entryCopy.confirmedClientId && entryCopy.client?.name && entryCopy.client?.externalId) {
    entryCopy.client = includeIntacctId(entryCopy.client);
  }
  if (entryCopy.confirmedProjectId && entryCopy.project?.name && entryCopy.project?.externalId) {
    entryCopy.project = includeIntacctId(entryCopy.project);
  }
  if (entryCopy.confirmedTaskId && entryCopy.task?.name && entryCopy.task?.externalId) {
    entryCopy.task = includeIntacctId(entryCopy.task);
  }
  if (entryCopy.confirmedItemId && entryCopy.item?.name && entryCopy.item?.externalId) {
    entryCopy.item = includeIntacctId(entryCopy.item);
  }
  // TimeType does not have intacctId
  entryCopy.dimensionsValues = entryCopy.dimensionsValues?.map((dimValue) => includeIntacctId(dimValue));
  return entryCopy;
};

const rowMinWidth = (companyData: Record<string, any>) => {
  return [
    companyData.showDepartmentLocation,
    companyData.showBillable,
    companyData.showCustom,
    companyData.showProject,
    companyData.showTask,
    companyData.showItem,
    companyData.showTimeType,
  ].reduce((acc, cur, idx) => {
    if (cur) {
      if (idx === 0) return acc + 300;
      if (idx === 1) return acc + 50;
      return acc + 150;
    }
    return acc;
  }, 300);
};

export {
  createDeepEqualSelector,
  filterAssignedClusters,
  filterUnAssignedClusters,
  groupAndSortClusters,
  includeIntacctIdInClusterDimensionNames,
  includeIntacctIdInEntryDimensionNames,
  rowMinWidth,
};
